import React from 'react';
import {
  Box,
  ButtonBase,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ChevronRightRounded as ChevronRightIcon } from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import { getTimelineMetaForCategory } from '../../constants/logTypeRegistry';
import { getAdaptiveCareLensKey } from '../Dashboard/shared/adaptiveQuickLogConfig';
import {
  ASK_ACCENT_DARK,
  ASK_COLORS,
} from './askRecordUi';

const DEFAULT_PROMPT_CARDS = [
  {
    category: 'sleep',
    question: ({ firstName }) => `How did ${firstName} sleep this week?`,
  },
  {
    category: 'behavior',
    question: () => 'Show me behavior patterns this month',
  },
  {
    category: 'medication',
    question: ({ firstName }) => `What meds is ${firstName} currently on?`,
  },
  {
    category: 'milestone',
    question: () => 'Has anything improved since last month?',
  },
];

const HOSPICE_PROMPT_CARDS = [
  {
    category: 'sleep',
    question: ({ firstName }) => `How has ${firstName}'s rest been this week?`,
  },
  {
    category: 'behavior',
    question: ({ firstName }) => `What comfort changes should we mention for ${firstName}?`,
  },
  {
    category: 'medication',
    question: () => 'Which comfort meds were given recently?',
  },
  {
    category: 'health',
    question: () => 'What changed with pain, breathing, or appetite?',
  },
];

const getPromptCards = (child) => (
  getAdaptiveCareLensKey(child) === 'hospice'
    ? HOSPICE_PROMPT_CARDS
    : DEFAULT_PROMPT_CARDS
);

const getPromptText = (prompt, firstName) => (
  typeof prompt.question === 'function'
    ? prompt.question({ firstName })
    : prompt.question
);

const AskRecordSuggestedQuestions = ({
  child,
  firstName,
  loading,
  onAsk,
}) => {
  const promptCards = getPromptCards(child);

  return (
    <Box sx={{ position: 'relative', zIndex: 1, mt: 1.1, pt: 1, borderTop: `1px solid ${alpha(colors.semantic.ink, 0.08)}` }}>
      <Box sx={{ pt: 0.15 }}>
        <Typography sx={{ mb: 0.8, pl: 0.05, fontSize: '0.7rem', lineHeight: 1, fontWeight: 800, letterSpacing: '0.1em', color: alpha(colors.semantic.ink, 0.56) }}>
          TRY ASKING
        </Typography>
        <Stack spacing={0.55}>
          {promptCards.map((prompt) => {
            const promptText = getPromptText(prompt, firstName);
            const meta = getTimelineMetaForCategory(prompt.category);
            const Icon = meta.iconComponent;
            const promptColor = meta.color || ASK_ACCENT_DARK;
            return (
              <ButtonBase
                key={promptText}
                onClick={() => onAsk(promptText)}
                disabled={loading}
                sx={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: '44px minmax(0, 1fr) auto',
                  alignItems: 'center',
                  gap: 0.9,
                  p: 0.85,
                  borderRadius: '16px',
                  bgcolor: 'transparent',
                  border: 0,
                  boxShadow: 'none',
                  textAlign: 'left',
                  '&:hover': {
                    bgcolor: alpha(ASK_COLORS.sourceSurface, 0.78),
                  },
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: '13px',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: alpha(promptColor, 0.18),
                    color: promptColor,
                  }}
                >
                  {Icon ? <Icon sx={{ fontSize: 21 }} /> : null}
                </Box>
                <Typography sx={{ minWidth: 0, color: colors.semantic.ink, fontSize: { xs: '0.88rem', sm: '0.94rem' }, lineHeight: 1.18, fontWeight: 500 }}>
                  {promptText}
                </Typography>
                <ChevronRightIcon sx={{ color: alpha(colors.semantic.ink, 0.34), fontSize: 22 }} />
              </ButtonBase>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
};

export default AskRecordSuggestedQuestions;
